import React from 'react';
import { Lightbulb } from 'lucide-react';
import type { HeatmapCell } from '../api/campaignsApi';
import styles from '../pages/PerformancePage.module.css';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
const HOUR_LABELS = ['6a', '7a', '8a', '9a', '10a', '11a', '12p', '1p', '2p', '3p', '4p', '5p'];

function cellColor(value: number, max: number): string {
  if (max === 0 || value === 0) return 'var(--border)';
  const intensity = value / max;
  if (intensity > 0.5) return `rgba(52,211,153,${intensity * 0.7})`;
  if (intensity > 0.1) return `rgba(52,211,153,${intensity * 0.4})`;
  return 'var(--border)';
}

function formatHour(h: number): string {
  if (h === 0) return '12am';
  if (h === 12) return '12pm';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

interface DayHourHeatmapProps {
  cells: HeatmapCell[];
  aiInsight?: string | null;
}

export function DayHourHeatmap({ cells, aiInsight }: DayHourHeatmapProps) {
  const lookup = new Map<string, number>();
  let max = 0;
  for (const c of cells) {
    const key = `${c.dayOfWeek}-${c.hour}`;
    const prev = lookup.get(key) ?? 0;
    const val = prev + c.conversions;
    lookup.set(key, val);
    if (val > max) max = val;
  }

  return (
    <div className={styles.heatmapWrap}>
      <div className={styles.heatmapMeta}>
        <span>Conversions · Last 30 days</span>
      </div>

      <div className={styles.heatmapGrid} style={{ gridTemplateColumns: `40px repeat(${HOURS.length}, 1fr)` }}>
        {/* Hour header row */}
        <div />
        {HOUR_LABELS.map((h) => (
          <div key={h} className={styles.heatmapHourLabel}>{h}</div>
        ))}

        {/* Data rows */}
        {DAYS.map((day, dayIdx) => (
          <React.Fragment key={day}>
            <div className={styles.heatmapDayLabel}>{day}</div>
            {HOURS.map((hour) => {
              const val = lookup.get(`${dayIdx}-${hour}`) ?? 0;
              return (
                <div
                  key={hour}
                  className={styles.heatmapCell}
                  title={`${day} ${formatHour(hour)}: ${val} conversion${val !== 1 ? 's' : ''}`}
                  style={{ background: cellColor(val, max) }}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {aiInsight && (
        <div className={styles.heatmapInsight}>
          <Lightbulb size={14} aria-hidden /> {aiInsight}
        </div>
      )}
    </div>
  );
}
